import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import WhatsAppIcon from './WhatsAppIcon';
import { 
  Phone, Mail, MapPin, Send, User, 
  MessageSquare, Clock, Globe, Twitter, Instagram,
  Calendar, Shield, Award, CheckCircle, Zap, Home,
  Star, ArrowUp, Heart, Crown, Sparkles, Target,
  TrendingUp, Users, Briefcase, BookOpen, Package,
  Linkedin
} from 'lucide-react';

const Footer = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
    setIsVisible(true);

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: "الرئيسية", path: "/", icon: Home },
    { name: "من نحن", path: "/About", icon: User },
    { name: "خدماتنا", path: "/Services", icon: Briefcase },
    { name: "المدونة", path: "/Blog", icon: BookOpen },
    { name: "الباقات", path: "/Packages", icon: Package },
    { name: "حجز الاستشارات", path: "/ConsultationBooking", icon: MessageSquare },
    { name: "موقعنا", path: "https://www.google.com.sa/maps/place/%D8%B4%D8%B1%D9%83%D8%A9+%D8%A7%D9%86%D8%B3+%D8%B9%D8%A8%D8%AF%D8%A7%D9%84%D8%B1%D8%AD%D9%85%D9%86+%D8%AE%D9%8A%D8%A7%D8%B7+%D9%84%D9%84%D9%85%D8%AD%D8%A7%D9%85%D8%A7%D8%A9+%D9%88%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D8%B4%D8%A7%D8%B1%D8%A7%D8%AA+%D8%A7%D9%84%D9%82%D8%A7%D9%86%D9%88%D9%86%D9%8A%D8%A9%E2%80%AD/@21.7015558,39.1072042,17z/data=!4m6!3m5!1s0x15c3d9df5e180a41:0x9c90f7f9629941c9!8m2!3d21.7015558!4d39.1046293!16s%2Fg%2F11ydmz6jsm?hl=ar&entry=ttu&g_ep=EgoyMDI1MDgyNS4wIKXMDSoASAFQAw%3D%3D", icon: Shield }
  ];

  const services = [
    "الاستشارات القانونية",
    "التمثيل القضائي",
    "صياغة العقود",
    "تقسيم الثروات",
    "قضايا الأسرة",
    "الشركات والاستثمار"
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Background - Elegant Dark Green Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f2e1a] via-[#1a472a] to-[#0f2e1a]">
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 border border-white/20 rounded-full"></div>
          <div className="absolute top-40 right-32 w-24 h-24 border border-white/20 rotate-45"></div>
          <div className="absolute bottom-32 left-40 w-28 h-28 border border-white/20 rounded-full"></div>
        </div>
      </div>
      
      {/* Mobile Background - Same Gradient */}
      <div className="md:hidden absolute inset-0 bg-gradient-to-br from-[#0f2e1a] via-[#1a472a] to-[#0f2e1a]"></div>

      {/* Main Footer Content */}
      <div className="relative z-10">
        {/* Middle Section - Main Footer */}
        <div className="bg-black/20 md:bg-gradient-to-r md:from-[#0a3a23]/40 md:to-black/40 border-t border-white/10 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-12">
              
              {/* Company Info with Big Logo */}
              <div className="lg:col-span-1 md:col-span-2">
                <div className="mb-6">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 rounded-3xl overflow-hidden mb-6 sm:mb-8 mx-auto bg-white p-3 shadow-xl border-2 border-white/20">
                    <img 
                      src="/images/Logo/تصميم بدون عنوان - 2025-07-24T002454.362.png" 
                      alt="مكتب أنس للمحاماة" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 text-center">شركة أنس عبد الرحمن خياط</h3>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  روابط سريعة
                </h4>
                <div className="space-y-2 sm:space-y-3">
                  {quickLinks.map((link, index) => (
                    link.path.startsWith('http') ? (
                      <a
                        key={index}
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 sm:gap-3 text-white hover:text-emerald-200 transition-colors duration-300 group text-sm sm:text-base font-bold"
                      >
                        <link.icon className="w-4 h-4 group-hover:scale-110 transition-transform duration-300 text-white" />
                        <span>{link.name}</span>
                      </a>
                    ) : (
                      <Link
                        key={index}
                        to={link.path}
                        className="flex items-center gap-2 sm:gap-3 text-white hover:text-emerald-200 transition-colors duration-300 group text-sm sm:text-base font-bold"
                      >
                        <link.icon className="w-4 h-4 group-hover:scale-110 transition-transform duration-300 text-white" />
                        <span>{link.name}</span>
                      </Link>
                    )
                  ))}
                </div>
              </div>



              {/* Contact & Social */}
              <div>
                <h4 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  تواصل معنا
                </h4>
                
                <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                  <div className="flex items-center gap-2 sm:gap-3 text-white text-sm sm:text-base font-bold">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                    <span>جدة، المملكة العربية السعودية</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-white text-sm sm:text-base font-bold">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                    <span>الأحد - الخميس: 8:00 ص - 6:00 م</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-white text-sm sm:text-base font-bold">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                    <a 
                      href="mailto:info@khayat.sa" 
                      className="hover:text-emerald-200 transition-colors duration-300"
                    >
                      info@khayat.sa
                    </a>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-white text-sm sm:text-base font-bold">
                    <WhatsAppIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                    <a 
                      href="https://wa.me/966115115945" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-emerald-200 transition-colors duration-300"
                    >
                      5945 511 11 966+
                    </a>
                  </div>
                </div>

                {/* Social Media */}
                <div className="space-y-3">
                  <h5 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">تابعنا على</h5>
                  <div className="flex gap-2 sm:gap-3">
                    <a
                      href="https://x.com/khayatlawfirm?t=jAXcLytECVDZ2DRxkSLPWg&s=09"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0a3a23] hover:bg-[#0a3a23]/80 rounded-xl flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                    
                    <a
                      href="https://www.instagram.com/khayatlawfirm?igsh=MTRmNXByeDBnOGxucw=="
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0a3a23] hover:bg-[#0a3a23]/80 rounded-xl flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                    >
                      <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>

                    <a
                      href="https://www.linkedin.com/company/108289700/admin/dashboard/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0a3a23] hover:bg-[#0a3a23]/80 rounded-xl flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                    >
                      <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>

                    <a
                      href="https://www.tiktok.com/@khayatlawfirm?_t=ZS-8z0ulBa0oyN&_r=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0a3a23] hover:bg-[#0a3a23]/80 rounded-xl flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.05-2.83-.31-4.08-1.03-2.25-1.21-3.89-3.84-3.89-6.34 0-1.97.81-3.91 2.22-5.27 1.37-1.31 3.26-2.05 5.21-2.08.99-.02 1.98.2 2.88.67.02-.33.03-.66.03-1z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Copyright */}
        <div className="bg-black/20 md:bg-gradient-to-r md:from-[#0a3a23]/40 md:to-black/40 border-t border-white/10 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:gap-6 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-white text-center sm:text-right">
                <p className="flex items-center gap-2 text-sm sm:text-base">
                  <span>جميع الحقوق محفوظة © {currentYear}</span>
                  <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  <span>شركة أنس عبد الرحمن خياط</span>
                </p>
              </div>
              
              <div className="flex items-center gap-4 text-white">
                <span className="flex items-center gap-2 text-sm sm:text-base">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  <span>رؤية 2030</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-4 sm:bottom-8 left-4 sm:left-8 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-[#0a3a23] hover:bg-[#0a3a23]/80 text-white rounded-full shadow-2xl transition-all duration-300 hover:scale-110 ${
          scrollY > 300 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
      >
        <ArrowUp className="w-5 h-5 sm:w-6 sm:h-6 mx-auto" />
      </button>
    </footer>
  );
};

export default Footer; 